import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';

import { apiRequest } from '@/src/services';
import { Container, Loading, ErrorMessage } from '@/src/ui';
import ClassMatrixReportFilters from './components/ClassMatrixFilters';
import ClassMatrixReportDataTable from './components/ClassMatrixTable';
import ClassMatrixLegend from './components/ClassMatrixLegend';
import type { ClassMatrixDTO } from './models/classMatrixDTOs';
import type { ClassMatrixReportFilterState } from './models/filterDTOs';
import QuestionDetailsModal from './components/QuestionDetailsModal';
import { useAuth } from '@/src/app/context/useAuth';

import './ClassMatrixPage.css';

const ClassMatrixReportPage: React.FC = () => {
    document.title = 'Class Matrix Report';

    const [data, setData] = useState<ClassMatrixDTO | null>(null);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');

    const { user } = useAuth();
    const userId = user?.id || 'nouser';
    const UNIVERSAL_FILTER_KEY = `universal-filters-${userId}`;

    const [filterState, setFilterState] = useState<ClassMatrixReportFilterState>(() => {
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    districtId: parsed.districtId || 0,
                    schoolId: parsed.schoolId || 0,
                    gradeLevelMasterId: parsed.gradeLevelMasterId || 0,
                    classId: parsed.classId || 0,
                    examTypeMasterId: 0,
                    examId: 0,
                };
            }
        } catch {
            // intentionally empty
        }
        return {
            districtId: 0,
            schoolId: 0,
            gradeLevelMasterId: 0,
            classId: 0,
            examTypeMasterId: 0,
            examId: 0,
        };
    });

    useEffect(() => {
        if (!userId) return;
        const universalFilters = {
            gradeLevelMasterId: filterState.gradeLevelMasterId,
            districtId: filterState.districtId,
            schoolId: filterState.schoolId,
            classId: filterState.classId,
        };
        try {
            localStorage.setItem(UNIVERSAL_FILTER_KEY, JSON.stringify(universalFilters));
        } catch {
            // intentionally empty
        }
    }, [filterState, userId, UNIVERSAL_FILTER_KEY]);

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(null);

    const handleQuestionClick = (questionId: number) => {
        setSelectedQuestionId(Number(questionId));
        setModalOpen(true);
    };

    const refreshTableData = useCallback(
        async (filters: ClassMatrixReportFilterState = filterState) => {
            setError('');
            setLoaded(false);

            if (!filters.districtId || !filters.schoolId || !filters.classId || !filters.examId) {
                setError('Please select Grade, District, School, Class, and Exam.');
                setLoaded(true);
                return;
            }

            let requestUrl = `reports/classMatrix?districtId=${filters.districtId}`;
            requestUrl += `&schoolId=${filters.schoolId}`;
            requestUrl += `&gradeLevelMasterId=${filters.gradeLevelMasterId}`;
            requestUrl += `&classId=${filters.classId}`;
            requestUrl += `&examId=${filters.examId}`;
            requestUrl += `&examTypeMasterId=${filters.examTypeMasterId}`;

            try {
                const res = await apiRequest<ClassMatrixDTO>('GET', requestUrl);
                setData(res.data ?? null);
            } catch {
                setError('Failed to load Class Matrix Report');
                setData(null);
            } finally {
                setLoaded(true);
            }
        },
        [filterState]
    );

    useEffect(() => {
        if (
            filterState.districtId &&
            filterState.schoolId &&
            filterState.classId &&
            filterState.examId
        ) {
            refreshTableData();
        } else {
            setData(null);
            setLoaded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Container>
            <h1 className="page-header">Class Matrix Report</h1>
            <br />

            <Box>
                <ClassMatrixReportFilters
                    filterState={filterState}
                    setFilterState={setFilterState}
                    refreshTableData={refreshTableData}
                />
            </Box>

            {!loaded && <Loading message={'Loading Class Matrix Report...'} />}
            {error && (
                <Box sx={{ textAlign: 'center', mb: 1 }}>
                    <ErrorMessage error={error} />
                </Box>
            )}

            <Box sx={{ pb: 2, display: 'flex', justifyContent: 'start' }}>
                <div>
                    <ClassMatrixLegend dataLoaded={data !== null && data.questions.length !== 0} />
                </div>
                {filterState.examId !== null && filterState.examId !== 0 && (
                    <div className="ml-auto">
                        <Button
                            onClick={() => null}
                            sx={{
                                textTransform: 'none',
                            }}
                            variant="outlined"
                            href={`/reports/questionList/${filterState.examId}`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            View Assessment
                        </Button>
                    </div>
                )}
            </Box>

            <div className="table-container" style={{ overflow: 'auto' }}>
                {loaded && data && (
                    <ClassMatrixReportDataTable data={data} onQuestionClick={handleQuestionClick} />
                )}
            </div>

            {selectedQuestionId && (
                <QuestionDetailsModal
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    filterState={filterState}
                    questionId={selectedQuestionId}
                />
            )}
        </Container>
    );
};

export default ClassMatrixReportPage;
